import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import addDays from 'date-fns/add_days';
import format from 'date-fns/format';
import {
  divide as _divide,
  escape as _escape,
  filter as _filter,
  get as _get,
  round as _round,
  uniq as _uniq
} from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import { PolicyUtilService } from '@amfam/policy/e-signature/data-access';
import {
  ANSRequestModel,
  CustomerEmailRequest,
  MessageModel,
  MessageRequest,
  NotificationRequest,
  NotificationRequestCustomerEmail,
  Policy,
  ReplaceVehicleRequestCustomerEmail,
  RiskModel,
  SubmitOnlineBindRequestModel
} from '@amfam/policy/models';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery, fromAgentActions, initialAgent } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, ErrorLevels, ErrorObj, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';
import { ReplaceVehicleService } from '../../../core/store/replace-vehicle/replace-vehicle.service';

@Component({
  selector: 'ds-replace-vehicle-review',
  templateUrl: './replace-vehicle-review.component.html',
  styleUrls: ['./replace-vehicle-review.component.scss'],
  standalone: false
})
export class ReplaceVehicleReviewComponent implements OnInit, OnDestroy {
  premiumDetails: any = {
    quoteExpirationDate: new Date(),
    current: {},
    new: {}
  };

  private vehicleId;
  private partnerId: string;
  private customerId: string;
  private customerEmail: string;
  private customerName: string;
  private customerFirstName: string;
  private customerLastName: string;
  private customerPhone: string;

  private selectedPolicy: Policy;
  private selectedProducer: Agent;

  public newVehicle: string;
  public newVehicleVin: string;
  public currentVehicle = 'current vehicle';
  private matchingvehicle: RiskModel;
  private stateCode: string;

  private drivers: string;
  private coverages: string;
  private discounts: string;
  private fees: string;

  errorMessage: string;
  public errorList: Array<ErrorObj> = [];

  private isUsedForBusiness: NotificationRequest['isUsedForBusiness'];
  private isOwner: NotificationRequest['isOwner'];
  private hasTPI: NotificationRequest['hasTPI'];
  private existingTPI: NotificationRequest['existingTPI'];
  private copyTPIIndicator: NotificationRequest['copyTPIIndicator'];

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private store: Store<fromRoot.RootState>,
    public roleService: ImpersonateRolesService,
    private replaceVehicleService: ReplaceVehicleService,
    private copyService: CopyService,
    private policyUtilService: PolicyUtilService,
    private utilService: UtilService,
    protected currencyPipe: CurrencyPipe
  ) {}

  ngOnInit() {
    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Replace a Vehicle');

    combineLatest(
      this.route.params,
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(PolicySelectors.selectAdvanceAutoPolicyRisks),
      this.store.select(fromRoot.getReplaceVehiclePremium),
      this.store.select(fromRoot.getReplaceVehicleNewVehicle),
      this.store.select(vehicleQuery.selectNewVehicle),
      this.store.select(userQuery.selectUserStateCode),
      this.store.select(vehicleQuery.selectCriteria),
      this.store.select(agentQuery.getAgentEntities),
      (
        params,
        policies,
        risks,
        newPremium,
        replaceVehicleNewVehicle,
        vehicleSelectNewVehicle,
        userState,
        vehicleSelectionDetails,
        agents
      ) => {
        return {
          vehicleId: params['id'],
          policies: policies,
          vehicles: risks,
          newPremium: newPremium,
          replaceVehicleNewVehicle: replaceVehicleNewVehicle,
          vehicleSelectNewVehicle: vehicleSelectNewVehicle,
          userState: userState,
          vehicleSelectionDetails,
          agents: agents
        };
      }
    )
      .pipe(take(1))
      .subscribe(state => {
        this.vehicleId = state.vehicleId;
        this.stateCode = state.userState;
        let matchingPolicy;

        const matchingVehicle = state.vehicles.find(vehicle => {
          return _get(vehicle, 'id') === this.vehicleId;
        });

        if (matchingVehicle) {
          const policyNumber = matchingVehicle.policyNumber;
          this.matchingvehicle = matchingVehicle;

          matchingPolicy = state.policies.find(policy => {
            return _get(policy, 'policyNumber') === policyNumber;
          });

          if (matchingPolicy) {
            // TODO - this needs to be more error proof:
            const currentTermMonths = Number(matchingPolicy.termDescription.split(' ')[0]);
            const monthlyCost = _round(_divide(matchingPolicy.totalPremium, currentTermMonths), 2);
            this.premiumDetails.current.description = 'Current';
            this.premiumDetails.current.fullCost = Number(
              _get(matchingPolicy, 'totalPremium', 0)
            ).toFixed(2);
            this.premiumDetails.current.policyTermMonths = currentTermMonths + ' months';
            this.premiumDetails.current.monthlyCost = monthlyCost.toFixed(2);

            this.premiumDetails.new.description = 'Quote';
            this.premiumDetails.new.fullCost = Number(
              _get(state, 'newPremium.totalPremium', 0)
            ).toFixed(2);
            this.premiumDetails.new.policyTermMonths = state.newPremium.termMonths + ' months';
            this.premiumDetails.new.monthlyCost = Number(
              _get(state, 'newPremium.monthlyPremium', 0)
            ).toFixed(2);
            this.premiumDetails.new.newVehicle = state.replaceVehicleNewVehicle;

            this.premiumDetails.orderNumber = state.newPremium.workOrderNumber;

            if (
              this.matchingvehicle &&
              this.matchingvehicle.year &&
              this.matchingvehicle.make &&
              this.matchingvehicle.model
            ) {
              this.currentVehicle =
                this.matchingvehicle.year +
                ' ' +
                this.matchingvehicle.make +
                ' ' +
                this.matchingvehicle.model;
            }

            this.newVehicle =
              _get(state, 'newVehicle.year', _get(state, 'vehicleSelectNewVehicle.year', '')) +
              ' ' +
              _get(state, 'newVehicle.make', _get(state, 'vehicleSelectNewVehicle.make', '')) +
              ' ' +
              _get(state, 'newVehicle.model', _get(state, 'vehicleSelectNewVehicle.model', ''));
            this.newVehicleVin = _get(
              state,
              'newVehicle.vin',
              _get(state, 'vehicleSelectNewVehicle.vin', '')
            );
            this.selectedPolicy = matchingPolicy;
            // JP: Check if we've loaded producer already - dispatch load if not
            this.selectedProducer =
              state.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
            if (!this.selectedProducer) {
              this.store.dispatch(
                new fromAgentActions.LoadAgentAction(
                  Object.assign({}, initialAgent, {
                    id: _get(this.selectedPolicy, 'assignedProducer.producerIdNum')
                  })
                )
              );
            }

            this.store.dispatch(
              new replaceVehicleActions.SendAnalyticsAction({
                pageAnalyticName: 'NewPremium',
                eventName: 'complete',
                referenceNumber: _get(this.premiumDetails, 'orderNumber')
              })
            );

            this.isUsedForBusiness = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.underWritingQuestionIsBusinessUse'
            );

            this.isOwner = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.underWritingQuestionIsOwner'
            );

            this.hasTPI = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.underWritingQuestionHasLoan',
              false
            );

            this.existingTPI = _get(state, 'vehicleSelectionDetails.TPIName', '');

            this.copyTPIIndicator =
              _get(state, 'vehicleSelectionDetails.copyTPIIndicator', '') === 'yes' ? 'Yes' : 'No';
          }
        }
      });

    // JP: After Load action has been dispatched, subscribe again and fetch the correct producer
    if (!this.selectedProducer) {
      combineLatest(
        this.store.select(agentQuery.getAreAllAgentsLoaded),
        this.store.select(agentQuery.getAgentEntities),
        (loaded, agents) => {
          return {
            loaded: loaded,
            agents: agents
          };
        }
      )
        .pipe(
          filter(data => data.loaded),
          takeUntil(this.stop$)
        )
        .subscribe(data => {
          this.selectedProducer =
            data.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
        });
    }

    combineLatest(
      this.store.select(userQuery.selectCustomerId),
      this.store.select(userQuery.selectEmailAddress),
      this.store.select(userQuery.selectPrimaryPhone),
      this.store.select(userQuery.selectFirstName),
      this.store.select(userQuery.selectLastName),
      this.store.select(BrandSelectors.selectPartnerId),
      this.store.select(BrandSelectors.selectIsPartner),
      (
        customerId,
        userEmailAddress,
        userPrimaryPhone,
        userFirstName,
        userLastName,
        partner,
        isPartner
      ) => ({
        customerId,
        userEmailAddress,
        userPrimaryPhone,
        userFirstName,
        userLastName,
        partner,
        isPartner
      })
    ).subscribe(res => {
      this.partnerId = res.partner;
      this.customerFirstName = _get(res, 'userFirstName', '');
      this.customerLastName = _get(res, 'userLastName', '');
      this.customerName = this.customerFirstName + ' ' + this.customerLastName;
      this.customerId = _get(res, 'user.customerId', '');
      this.customerEmail = _get(res, 'userEmailAddress', '');
      this.customerPhone = _get(
        res,
        'userPrimaryPhone.tenDigitPhoneNumber',
        'No phone number on record'
      );
    });

    this.store
      .select(fromRoot.getReplaceVehicleHasError)
      .pipe(takeUntil(this.stop$))
      .subscribe(error => {
        if (error) {
          this.showErrorMessage();
          this.store.dispatch(
            new replaceVehicleActions.SendAnalyticsAction({
              pageAnalyticName: 'Error'
            })
          );
        }
      });
  }

  private getPartnerData(): Observable<any> {
    return combineLatest(
      this.store.select(BrandSelectors.selectPartnerId),
      this.store.select(BrandSelectors.selectIsPartner)
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  cancel() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies']
      })
    );
  }

  getAnotherQuoteClick() {
    this.store.dispatch(
      new replaceVehicleActions.SendAnalyticsAction({
        buttonAnalyticName: this.errorList.length > 0 ? 'ErrorGetAnotherQuote' : 'GetAnotherQuote'
      })
    );

    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies/risk-selection']
      })
    );
  }

  /**
   * AS: This function will be called when the customer clicks on sent to agent button.
   */
  submit() {
    const customerNotificationRequest: NotificationRequestCustomerEmail = {
      email: new ReplaceVehicleRequestCustomerEmail(this.getCustomerEmailRequestModel()),
      message: new MessageModel(this.getMessageModel()),
      templateId: 'ReplaceVehicleQuoteSubmitted'
    };

    customerNotificationRequest.message.policyNumber = this.utilService.maskPolicyNumber(
      _get(this.selectedPolicy, 'policyNumber', '')
    );

    const customerName = this.customerName;
    const policyNumber = _get(this.selectedPolicy, 'policyNumber', '');
    const newVehicle = customerNotificationRequest.message.newRiskInformation;
    const oldVehicle = customerNotificationRequest.message.riskInformation;
    const existingVehicleVin = customerNotificationRequest.message.existingVehicleVin;
    const newVehicleVin = customerNotificationRequest.message.newVehicleVin;
    const orderNumber = _get(this.premiumDetails, 'orderNumber');
    const msrp = customerNotificationRequest.message.msrp || 'N/A';
    const estimatedVehiclePrice =
      customerNotificationRequest.message.estimatedVehicleValue || 'N/A';
    const notificationMsg = this.copyService.getCopy('policy.agentInformationANS', {
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      orderNumber: orderNumber,
      existingVehicleVin: existingVehicleVin,
      newVehicleVin: newVehicleVin,
      msrp: this.formatMSRP(msrp) || 'N/A',
      estimatedVehiclePrice: estimatedVehiclePrice
    });

    const ansRequest: ANSRequestModel = new ANSRequestModel({
      producer: this.selectedProducer,
      orderNumber: _get(this.premiumDetails, 'orderNumber'),
      policyNumber: _get(this.selectedPolicy, 'policyNumber'),
      from: this.copyService.getCopy('shared.myAccountTxt'),
      customerName: this.customerName,
      notificationText: notificationMsg,
      vehicleId: this.vehicleId
    });

    let agentNotificationRequest: NotificationRequest = {
      producer: this.selectedProducer,
      vehicleId: this.vehicleId,
      customerName: customerName,
      policyNumber: policyNumber,
      orderNumber: _get(this.premiumDetails, 'orderNumber'),
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      email: this.customerEmail,
      type: 'Quote to agent',
      copyTPIIndicator: this.copyTPIIndicator,
      isUsedForBusiness: this.isUsedForBusiness,
      isOwner: this.isOwner,
      hasTPI: this.hasTPI,
      existingTPI: this.existingTPI,
      existingVehicleVin: existingVehicleVin,
      newVehicleVin: newVehicleVin,
      msrp: msrp,
      estimatedVehiclePrice: estimatedVehiclePrice
    };

    const existingTPI =
      this.copyTPIIndicator === 'Yes'
        ? _get(this, 'existingTPI', '')
        : this.copyService.getCopy('policy.policyChangeAgentFollowUpText');

    const notificationMsgANS = this.copyService.getCopy('policy.ANSContentQuoteToAgent', {
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      email: this.customerEmail,
      isUsedForBusiness: this.isUsedForBusiness,
      isOwner: this.isOwner,
      hasTPI: this.hasTPI,
      existingTPI: this.hasTPI === 'Yes' ? existingTPI : 'Not Applicable',
      orderNumber: orderNumber
    });

    this.store.dispatch(
      new replaceVehicleActions.ReplaceVehicleANSNotifyAgentAction(
        Object.assign({}, ansRequest, { notificationText: notificationMsgANS })
      )
    );

    // Dispatch an action to notify the agents and customers via email.
    this.store.dispatch(
      new replaceVehicleActions.ReplaceVehicleNotifyAgentAction(agentNotificationRequest)
    );
    this.store.dispatch(
      new replaceVehicleActions.ReplaceVehicleNotifyCustomerAction(customerNotificationRequest)
    );

    this.store.dispatch(
      new replaceVehicleActions.SendAnalyticsAction({
        buttonAnalyticName: 'SendToMyAgent'
      })
    );
  }

  /**
   * AS: This function will be called when the customer clicks on sent to bind now button.
   */
  submitOnlineBind() {
    const customerNotificationRequest: NotificationRequestCustomerEmail = {
      email: new ReplaceVehicleRequestCustomerEmail(this.getCustomerEmailRequestModel()),
      message: new MessageModel(this.getMessageModel()),
      templateId: 'ReplaceVehicleBound'
    };

    customerNotificationRequest.message.policyNumber = this.utilService.maskPolicyNumber(
      _get(this.selectedPolicy, 'policyNumber', '')
    );

    const customerName = this.customerName;
    const policyNumber = _get(this.selectedPolicy, 'policyNumber', '');
    const newVehicle = customerNotificationRequest.message.newRiskInformation;
    const oldVehicle = customerNotificationRequest.message.riskInformation;
    const existingVehicleVin = customerNotificationRequest.message.existingVehicleVin;
    const newVehicleVin = customerNotificationRequest.message.newVehicleVin;
    const orderNumber = _get(this.premiumDetails, 'orderNumber');

    const hasTPIChanged = this.hasTPI === 'Yes' && this.copyTPIIndicator === 'No';
    const msrp = customerNotificationRequest.message.msrp;
    const estimatedVehiclePrice = customerNotificationRequest.message.estimatedVehicleValue;

    const copyPath = hasTPIChanged
      ? 'policy.ANSContentQuoteTPIChangeNeeded'
      : 'policy.ANSContentQuoteNoTPIChangeNeeded';

    const emailSubjectNoActionNeeded = this.copyService.getCopy(
      'policy.emailSubjectOnlineBindNoAction'
    );
    const emailSubjectActionNeeded = this.copyService.getCopy(
      'policy.emailSubjectOnlineBindActionNeeded'
    );

    const existingTPI =
      this.copyTPIIndicator === 'Yes'
        ? _get(this, 'existingTPI', '')
        : this.copyService.getCopy('policy.policyChangeAgentFollowUpText');

    const notificationMsg = this.copyService.getCopy(copyPath, {
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      email: this.customerEmail,
      isUsedForBusiness: this.isUsedForBusiness,
      isOwner: this.isOwner,
      hasTPI: this.hasTPI,
      existingTPI: this.hasTPI === 'Yes' ? existingTPI : 'Not Applicable',
      orderNumber: orderNumber,
      existingVehicleVin: existingVehicleVin,
      newVehicleVin: newVehicleVin,
      msrp: this.formatMSRP(msrp) || 'N/A',
      estimatedVehiclePrice: this.formatMSRP(estimatedVehiclePrice) || 'N/A'
    });

    const agentNotificationRequest: NotificationRequest = {
      producer: this.selectedProducer,
      orderNumber: _get(this.premiumDetails, 'orderNumber'),
      vehicleId: this.vehicleId,
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      email: this.customerEmail,
      copyTPIIndicator: this.copyTPIIndicator,
      type: 'Quote',
      isUsedForBusiness: this.isUsedForBusiness,
      isOwner: this.isOwner,
      hasTPI: this.hasTPI,
      existingTPI: this.existingTPI,
      existingVehicleVin: existingVehicleVin,
      newVehicleVin: newVehicleVin,
      msrp: msrp,
      estimatedVehiclePrice: estimatedVehiclePrice
    };

    const internalAgentEmail = _get(this.selectedProducer, 'agencyEmails[0]')
      ? env.policyGracePeriodNotificatonInternalAgencyMailbox
      : env.policyGracePeriodNotificatonInternal;

    const onlineBindReqPayload: SubmitOnlineBindRequestModel = {
      policyNumber: _get(this.selectedPolicy, 'policyNumber', ''),
      state: this.stateCode,
      notificationSubject: hasTPIChanged ? emailSubjectActionNeeded : emailSubjectNoActionNeeded,
      notificationText: notificationMsg,
      notificationType: 'ANSMYABIND',
      emailSubject: hasTPIChanged ? emailSubjectActionNeeded : emailSubjectNoActionNeeded,
      emailBody: _escape(
        this.replaceVehicleService.generateInternalEmailContent(agentNotificationRequest)
      ),
      workOrderNumber: orderNumber,
      vehicleId: this.vehicleId,
      sourceSystem: 'MyAccountWebApplication',
      customer: {
        firstName: this.customerFirstName,
        lastName: this.customerLastName,
        phone: this.customerPhone,
        email: this.customerEmail,
        emailTemplateId: customerNotificationRequest.templateId
      },
      producer: {
        firstName: this.selectedProducer.firstName,
        lastName: this.selectedProducer.lastName,
        producerId: this.selectedProducer.id,
        phone: _get(
          this.selectedProducer,
          'phones[0].phoneNumber',
          _get(this.selectedProducer, 'phones[0].phoneNumber', 'No phone number on record')
        ),
        fromAddress: env.policyChangeRequestInternal,
        fromAddressLabel: this.copyService.getCopy('policy.policyChangeAgentEmailLabel'),
        email: env.production
          ? _get(
              this.selectedProducer,
              'agencyEmails[0]',
              _get(this.selectedProducer, 'emails[0]', 'No email address on record')
            )
          : internalAgentEmail
      },
      emailTemplateData: {
        RiskInformation: _get(this.matchingvehicle, 'description', ''),
        TotalPremium: _get(this.premiumDetails, 'current.fullCost', null),
        MonthlyPremium: _get(this.premiumDetails, 'current.monthlyCost', null),
        TermDescription: _get(this.premiumDetails, 'current.policyTermMonths', ''),
        NewRiskInformation: _get(this, 'newVehicle', ''),
        NewTotalPremium: _get(this.premiumDetails, 'new.fullCost', null),
        NewMonthlyPremium: _get(this.premiumDetails, 'new.monthlyCost', null),
        NewTermDescription: _get(this.premiumDetails, 'new.policyTermMonths', ''),
        Drivers: _get(this, 'drivers', ''),
        GaragedAddress: _get(this.matchingvehicle, 'address.addressLine1'),
        Coverages: _get(this, 'coverages', ''),
        Fees: _get(this, 'fees', ''),
        Discounts: _get(this, 'discounts', ''),
        PolicyNumber: customerNotificationRequest.message.policyNumber
      }
    };

    this.store.dispatch(
      new replaceVehicleActions.ReplaceVehicleSubmitOnlineBindAction(onlineBindReqPayload)
    );
  }

  private getCustomerEmailRequestModel(): CustomerEmailRequest {
    const customerEmailRequest: CustomerEmailRequest = {
      customerEmailId: this.customerEmail,
      customerId: this.customerId,
      partnerId: this.partnerId
    };
    return customerEmailRequest;
  }

  private getMessageModel(): MessageRequest {
    for (const vehicleNamedInsured of _uniq(
      _filter(this.matchingvehicle.vehicleRoles, { roleType: 'PrimaryDriver' })
    )) {
      if (this.drivers) {
        this.drivers += ', ' + _get(vehicleNamedInsured, 'name');
      } else {
        this.drivers = _get(vehicleNamedInsured, 'name');
      }
    }
    for (const vehicleCoverage of _uniq(_get(this.matchingvehicle, 'vehicleCoverages', []))) {
      if (this.coverages) {
        this.coverages += ', ' + _get(vehicleCoverage, 'name');
      } else {
        this.coverages = _get(vehicleCoverage, 'name');
      }
    }
    // Pull vehicle-level discounts
    for (const vehicleDiscount of _uniq(_get(this.matchingvehicle, 'vehicleDiscounts', []))) {
      if (this.discounts) {
        this.discounts += ', ' + _get(vehicleDiscount, 'name');
      } else {
        this.discounts = _get(vehicleDiscount, 'name');
      }
    }
    // Pull policy-level discounts
    for (const policyDiscount of _uniq(_get(this.matchingvehicle, 'policy.policyDiscounts', []))) {
      if (this.discounts) {
        this.discounts += ', ' + _get(policyDiscount, 'name');
      } else {
        this.discounts = _get(policyDiscount, 'name');
      }
    }
    // Pull vehicle-level fees
    for (const vehicleFee of _uniq(_get(this.matchingvehicle, 'vehicleFees', []))) {
      if (this.fees) {
        this.fees += ', ' + _get(vehicleFee, 'name');
      } else {
        this.fees = _get(vehicleFee, 'name');
      }
    }
    // Pull policy-level fees
    for (const policyFee of _uniq(_get(this.matchingvehicle, 'policy.policyFees', []))) {
      if (this.fees) {
        this.fees += ', ' + _get(policyFee, 'name');
      } else {
        this.fees = _get(policyFee, 'name');
      }
    }
    for (const vehicleSurchanges of _uniq(
      _get(this.matchingvehicle, 'policy.policySurcharges', [])
    )) {
      if (this.fees) {
        this.fees += ', ' + _get(vehicleSurchanges, 'name');
      } else {
        this.fees = _get(vehicleSurchanges, 'name');
      }
    }

    const quoteExpDate: any = _get(this.premiumDetails, 'quoteExpirationDate');

    const messageModelRequest: MessageRequest = {
      firstName: this.customerFirstName,
      policyNumber: this.selectedPolicy.policyNumber,

      riskInformation: this.matchingvehicle.description,
      existingVehicleVin: this.matchingvehicle.vin,
      totalPremium: _get(this.premiumDetails, 'current.fullCost'),
      monthlyPremium: _get(this.premiumDetails, 'current.monthlyCost'),
      termDescription: _get(this.premiumDetails, 'current.policyTermMonths'),

      newRiskInformation: this.newVehicle,
      newVehicleVin: this.newVehicleVin,

      newTotalPremium: _get(this.premiumDetails, 'new.fullCost'),
      newMonthlyPremium: _get(this.premiumDetails, 'new.monthlyCost'),
      newTermDescription: _get(this.premiumDetails, 'new.policyTermMonths'),
      // AS: If we do not have the quote exp date coming back assume the current date and send the mail
      quoteExpirationDate: quoteExpDate
        ? format(addDays(new Date(quoteExpDate), 60), 'MM/DD/YYYY')
        : format(addDays(new Date(), 60), 'MM/DD/YYYY'),
      garagedAddress: _get(this.matchingvehicle, 'address.addressLine1'),

      drivers: this.drivers,
      discounts: this.discounts,
      fees: this.fees,
      coverages: this.coverages,
      orderNumber: _get(this.premiumDetails, 'orderNumber', ''),
      producerId: _get(this, 'selectedProducer.id', ''),
      msrp: this.premiumDetails?.new?.newVehicle?.msrp,
      estimatedVehicleValue: this.premiumDetails?.new?.newVehicle?.estimatedVehicleValue
    };
    return messageModelRequest;
  }

  private showErrorMessage() {
    this.errorList = [];
    this.errorList.push({
      errorMessage: this.copyService.getCopy('policy.onlineBindFailureMessage'),
      errorLevel: ErrorLevels.HIGH
    });
  }
  private formatMSRP(msrp: string) {
    if (!msrp) {
      return '';
    }
    const value = msrp.replace(/[^0-9.-]+/g, '');
    return this.currencyPipe.transform(value, 'USD', 'symbol', '1.0-2');
  }
}
